import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Building2, Palette, Key, Users, Award, Target, Heart, 
  CheckCircle, ArrowRight, Phone, Mail, MapPin, Star,
  Rocket, Globe, Code, Briefcase, Home, Flag
} from 'lucide-react';

// Founder Profile
const founder = {
  name: 'Kiran Tirupati',
  role: 'Founder & CEO',
  image: 'https://customer-assets.emergentagent.com/job_17a6b21b-e708-4d1d-9d12-dfba397aa4c2/artifacts/f4i0wn1i_profile-kiran.jpg',
  journey: [
    { year: '2007', role: 'Game Programmer', company: 'EA Mobile India', icon: Code },
    { year: '2010', role: 'Android Developer', company: 'Mobile Technology', icon: Code },
    { year: '2013', role: 'Moved to USA', company: 'Living the American Dream', icon: Globe },
    { year: '2013-2020', role: 'Tech Professional', company: 'Sprint, AT&T, Farmers Insurance, PetSmart', icon: Briefcase },
    { year: '2020', role: 'Software Architect', company: 'eBay Inc', icon: Rocket },
    { year: '2021', role: 'Entrepreneur', company: 'Forever Living International', icon: Target },
    { year: '2025', role: 'Founder & CEO', company: 'ZenStudios India', icon: Flag },
  ]
};

// Company Timeline
const companyMilestones = [
  { 
    year: '2018', 
    title: 'The Spark', 
    description: 'It all started with DIY home improvement projects—a passion for creating beautiful, functional spaces',
    icon: Home
  },
  { 
    year: '2021', 
    title: 'OM Home Services', 
    description: 'Founded in Phoenix, Arizona. Obtained General Contractor license and began serving residents with renovations, house maintenance, and rent-readiness solutions',
    icon: Building2
  },
  { 
    year: '2022', 
    title: 'Turnkey Solutions', 
    description: 'Expanded to provide complete residential turnkey solutions for properties managed by third-party companies. Launched interior design and storage solutions',
    icon: Key
  },
  { 
    year: 'Dec 2025', 
    title: 'ZenStudios India', 
    description: 'The big dream unfolded—established ZenStudios in Hyderabad to serve the people of India. Pilot launched in Hyderabad',
    icon: Rocket
  },
  { 
    year: '2026', 
    title: 'Expanding Horizons', 
    description: 'Currently spreading across both Telugu states—Telangana and Andhra Pradesh. Here\'s to more amazing things to come!',
    icon: Globe
  }
];

const values = [
  { icon: Target, title: 'Precision', description: 'Every project executed with meticulous attention to detail' },
  { icon: Heart, title: 'Passion', description: 'We love what we do and it shows in our work' },
  { icon: Users, title: 'Trust', description: 'Building lasting relationships with transparency' },
  { icon: Award, title: 'Excellence', description: 'Committed to delivering the highest quality' }
];

const stats = [
  { number: '18+', label: 'Years Tech Experience' },
  { number: '2', label: 'Countries Operations' },
  { number: '500+', label: 'Properties Listed' },
  { number: '200+', label: 'Interior Projects' }
];

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-white pt-20" data-testid="about-us-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-primary/5 via-white to-amber-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Building2 className="w-4 h-4" /> Our Story
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              From <span className="text-primary">Code</span> to <span className="text-amber-600">Construction</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8">
              A journey spanning continents, industries, and dreams—built one step at a time with passion, perseverance, and a commitment to serving communities.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <p className="text-3xl md:text-4xl font-bold mb-2">{stat.number}</p>
                <p className="text-primary-foreground/80 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Founder Image */}
            <div className="relative">
              <div className="aspect-square max-w-md mx-auto lg:mx-0 rounded-2xl overflow-hidden shadow-2xl ring-8 ring-primary/10">
                <img loading="lazy" decoding="async" 
                  src={founder.image}
                  alt={founder.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-5 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <Flag className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900">USA → India</p>
                    <p className="text-gray-500 text-sm">Building Back Home</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Founder Story */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-2">{founder.name}</h2>
              <p className="text-primary font-semibold text-lg mb-6">{founder.role}</p>
              
              <div className="prose prose-lg text-gray-600 space-y-4">
                <p>
                  My journey began in <strong>2007 as a Game Programmer at EA Mobile India</strong>—a world where creativity met code, and every pixel told a story.
                </p>
                <p>
                  In 2010, I transitioned to <strong>Android development</strong>, riding the wave of mobile revolution. Then came 2013—a leap of faith to the <strong>United States</strong>, inspired by the welcoming American culture and the spirit of entrepreneurship.
                </p>
                <p>
                  Living the American Dream, I built my network one step at a time, working with organizations like <strong>Sprint, AT&T, Farmers Insurance, PetSmart</strong>, rising to become a <strong>Software Architect at eBay Inc</strong>, and later joining <strong>Forever Living International</strong>.
                </p>
                <p>
                  But the heart always knows where it belongs. The dream of serving my nation called me back to India, and here I am—investing my time and experience into building <strong>ZenStudios</strong> to serve the people of my homeland.
                </p>
                <p className="text-primary font-semibold italic">
                  Here's to the amazing things yet to come! 🚀
                </p>
              </div>
            </div>
          </div>

          {/* Career Journey Timeline */}
          <div className="mt-20">
            <h3 className="text-2xl font-bold text-center mb-12">The Journey</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {founder.journey.map((item, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-3 bg-gray-50 hover:bg-primary/5 px-4 py-3 rounded-full transition-colors"
                >
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-sm">
                    <span className="font-bold text-primary">{item.year}</span>
                    <span className="text-gray-400 mx-1">•</span>
                    <span className="text-gray-700">{item.company}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Building2 className="w-4 h-4" /> Company History
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">How ZenStudios Was Born</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From DIY weekend projects to a full-fledged company serving two nations—this is the story of ZenStudios.
            </p>
          </div>

          {/* Company Timeline */}
          <div className="relative max-w-4xl mx-auto">
            <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 w-0.5 h-full bg-gradient-to-b from-amber-400 via-primary to-violet-500" />
            
            <div className="space-y-12">
              {companyMilestones.map((milestone, idx) => (
                <div key={idx} className={`relative flex items-start gap-8 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  {/* Timeline dot */}
                  <div className="absolute left-8 md:left-1/2 transform -translate-x-1/2 w-16 h-16 bg-white rounded-full border-4 border-primary shadow-lg flex items-center justify-center z-10">
                    <milestone.icon className="w-6 h-6 text-primary" />
                  </div>
                  
                  {/* Content */}
                  <div className={`flex-1 ml-24 md:ml-0 ${idx % 2 === 0 ? 'md:pr-20 md:text-right' : 'md:pl-20'}`}>
                    <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow inline-block">
                      <span className="inline-block bg-primary/10 text-primary font-bold px-3 py-1 rounded-full text-sm mb-3">
                        {milestone.year}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{milestone.title}</h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>
                  
                  {/* Spacer for alternating layout */}
                  <div className="hidden md:block flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These core principles guide everything we do at ZenStudios
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => (
              <div key={idx} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Two Nations, One Mission</h2>
              <p className="text-lg text-gray-600 mb-6">
                ZenStudios bridges continents with a unified vision: transforming spaces and lives through quality craftsmanship and innovative solutions.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-xl">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">🇺🇸</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">OM Home Services, USA</h4>
                    <p className="text-gray-600 text-sm">Phoenix, Arizona • General Contractor • Residential Turnkey Solutions</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">🇮🇳</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">ZenStudios, India</h4>
                    <p className="text-gray-600 text-sm">Hyderabad • Real Estate • Interior Design • Property Management</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img loading="lazy" decoding="async" 
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80"
                alt="Global Operations"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-4 -right-4 bg-primary text-white px-6 py-3 rounded-full font-semibold shadow-lg">
                Expanding in Telugu States
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What We Do</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive solutions for all your property and design needs
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group p-8 rounded-2xl bg-white border border-gray-100 hover:border-primary hover:shadow-xl transition-all">
              <Building2 className="w-12 h-12 text-primary mb-6" />
              <h3 className="text-2xl font-bold mb-4">Real Estate</h3>
              <p className="text-gray-600 mb-6">
                Buy, sell, or invest in properties with confidence. Our verified listings and expert guidance make property transactions seamless.
              </p>
              <ul className="space-y-2 mb-6">
                {['Verified property listings', 'Expert valuations', 'Legal assistance', 'Negotiation support'].map((item, i) => (
                  <li key={i} className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-primary mr-2" /> {item}
                  </li>
                ))}
              </ul>
              <Button asChild variant="outline" className="rounded-full group-hover:bg-primary group-hover:text-white">
                <Link to="/real-estate">Explore Properties</Link>
              </Button>
            </div>

            <div className="group p-8 rounded-2xl bg-white border border-gray-100 hover:border-amber-500 hover:shadow-xl transition-all">
              <Palette className="w-12 h-12 text-amber-500 mb-6" />
              <h3 className="text-2xl font-bold mb-4">Interior Design</h3>
              <p className="text-gray-600 mb-6">
                Transform your space with our expert designers. Get instant AI-powered quotes and bring your vision to life.
              </p>
              <ul className="space-y-2 mb-6">
                {['Z-Engine instant quotes', '3D visualizations', 'Premium materials', 'On-time delivery'].map((item, i) => (
                  <li key={i} className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-amber-500 mr-2" /> {item}
                  </li>
                ))}
              </ul>
              <Button asChild variant="outline" className="rounded-full group-hover:bg-amber-500 group-hover:text-white">
                <Link to="/interior-design">Get Design Quote</Link>
              </Button>
            </div>

            <div className="group p-8 rounded-2xl bg-white border border-gray-100 hover:border-violet-500 hover:shadow-xl transition-all">
              <Key className="w-12 h-12 text-violet-500 mb-6" />
              <h3 className="text-2xl font-bold mb-4">Property Management</h3>
              <p className="text-gray-600 mb-6">
                Sit back and relax while we manage your property. From finding tenants to maintenance, we handle it all.
              </p>
              <ul className="space-y-2 mb-6">
                {['Tenant screening', 'Rent collection', 'Maintenance', 'Legal compliance'].map((item, i) => (
                  <li key={i} className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-violet-500 mr-2" /> {item}
                  </li>
                ))}
              </ul>
              <Button asChild variant="outline" className="rounded-full group-hover:bg-violet-500 group-hover:text-white">
                <Link to="/property-management">Learn More</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 md:py-28 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Space?
          </h2>
          <p className="text-xl text-white/80 mb-12">
            Let&apos;s discuss how we can help you achieve your property and design goals.
          </p>
          <div className="flex flex-wrap gap-6 justify-center mb-12">
            <a href="tel:+918790460259" className="flex items-center gap-3 bg-white/10 backdrop-blur px-6 py-4 rounded-full text-white hover:bg-white/20 transition-colors">
              <Phone className="w-5 h-5" />
              +91 87904 60259
            </a>
            <a href="mailto:info@zenstudios.in" className="flex items-center gap-3 bg-white/10 backdrop-blur px-6 py-4 rounded-full text-white hover:bg-white/20 transition-colors">
              <Mail className="w-5 h-5" />
              info@zenstudios.in
            </a>
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur px-6 py-4 rounded-full text-white">
              <MapPin className="w-5 h-5" />
              Hyderabad, India
            </div>
          </div>
          <Button asChild size="lg" className="rounded-full bg-white text-primary hover:bg-white/90 px-12 py-6 text-lg">
            <Link to="/interior-design">
              Get Started Today <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
